import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing column filters
 * @param {Array} columnConfig - Array of column configuration objects
 * @param {string} persistenceKey - Key for localStorage persistence
 * @param {Function} onError - Optional error callback function (message, type)
 * @returns {Object} - Filter state and methods
 */
function useColumnFilters(columnConfig = [], persistenceKey = null, onError = null) {
  const { initialFilters = [] } = typeof columnConfig === 'object' && !Array.isArray(columnConfig) ? columnConfig : {};
  const config = Array.isArray(columnConfig) ? columnConfig : [];

  // Load persisted filters if available
  const loadPersistedFilters = useCallback(() => {
    if (!persistenceKey || typeof window === 'undefined') return {};
    try {
      const persisted = localStorage.getItem(`filters_${persistenceKey}`);
      return persisted ? JSON.parse(persisted) : {};
    } catch (err) {
      return {};
    }
  }, [persistenceKey]);

  const [filters, setFilters] = useState(() => {
    const persisted = loadPersistedFilters();
    return persisted || {};
  });

  const [currentFilter, setCurrentFilter] = useState(null);
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [tempFilters, setTempFilters] = useState({});

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!persistenceKey || typeof window === 'undefined') return;
    try {
      if (Object.keys(filters).length === 0) {
        localStorage.removeItem(`filters_${persistenceKey}`);
      } else {
        localStorage.setItem(`filters_${persistenceKey}`, JSON.stringify(filters));
      }
    } catch (err) {
      // silent error
    }
  }, [filters, persistenceKey]);

  // Convert filters object to activeFilters array
  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .map(([columnId, filterValues]) => {
        const column = config.find(col => col.id === columnId);
        if (!column) return null;

        // Format display value based on filter type
        let displayValue = filterValues.value;
        if (column.filterType === 'date' && filterValues.operator === 'between') {
          displayValue = `${filterValues.startDate || ''} - ${filterValues.endDate || ''}`;
        } else if (column.filterType === 'number' && filterValues.operator === 'between') {
          displayValue = `${filterValues.min || ''} - ${filterValues.max || ''}`;
        } else if (column.filterType === 'text' && filterValues.value) {
          // Auto-detect if multiple values and format display accordingly
          const value = filterValues.value.trim();
          const hasMultipleValues =
            /[\s,\n\r]+/.test(value) && value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

          if (hasMultipleValues) {
            // For multiple values, show count and first few values
            const values = value
              .split(/[\s,\n\r]+/)
              .map(v => v.trim())
              .filter(v => v.length > 0);

            if (values.length <= 3) {
              displayValue = values.join(', ');
            } else if (values.length <= 10) {
              displayValue = `${values.slice(0, 3).join(', ')} and ${values.length - 3} more`;
            } else {
              displayValue = `${values.length} values (${values.slice(0, 2).join(', ')}, ...)`;
            }
          } else {
            displayValue = value;
          }
        }

        return {
          id: columnId,
          columnId,
          label: column.label,
          value: displayValue,
          filterType: column.filterType,
          operator: filterValues.operator,
          ...filterValues,
        };
      })
      .filter(Boolean);
  }, [filters, config]);

  // Generate filter parameters for API calls
  const filterParams = useMemo(() => {
    const params = {};

    Object.entries(filters).forEach(([columnId, filterValues]) => {
      const column = config.find(col => col.id === columnId);
      if (!column) return;

      const { filterType, apiParam } = column;
      const apiKey = apiParam || columnId;

      switch (filterType) {
        case 'date':
          // For date filters, assume 'between' if startDate or endDate are present
          if (filterValues.startDate || filterValues.endDate || filterValues.operator === 'between') {
            if (filterValues.startDate) {
              params[`${apiKey}__gte`] = filterValues.startDate;
            }
            if (filterValues.endDate) {
              params[`${apiKey}__lte`] = filterValues.endDate;
            }
          } else if (filterValues.value) {
            const suffix = {
              equals: '__eq',
              after: '__gt',
              before: '__lt',
            }[filterValues.operator];
            if (suffix) {
              params[`${apiKey}${suffix}`] = filterValues.value;
            }
          }
          break;
        case 'text':
          if (filterValues.value) {
            const value = filterValues.value.trim();

            // Auto-detect if multiple values (contains comma, space, or line break separators)
            const hasMultipleValues =
              /[\s,\n\r]+/.test(value) && value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

            if (hasMultipleValues) {
              // Process multiple values separated by comma, space, or line break
              const cleanedValues = value
                .split(/[\s,\n\r]+/)
                .map(v => v.trim())
                .filter(v => v.length > 0);

              if (cleanedValues.length > 0) {
                params[`${apiKey}__in`] = cleanedValues.join(',');
              }
            } else {
              // Single value - use contains search
              params[`${apiKey}__icontains`] = value;
            }
          }
          break;
        case 'select':
          if (filterValues.value !== undefined && filterValues.value !== '') {
            params[apiKey] = filterValues.value;
          }
          break;
        case 'number':
          if (filterValues.operator === 'between') {
            if (filterValues.min !== undefined && filterValues.min !== '') {
              params[`${apiKey}__gte`] = filterValues.min;
            }
            if (filterValues.max !== undefined && filterValues.max !== '') {
              params[`${apiKey}__lte`] = filterValues.max;
            }
          } else if (filterValues.value !== undefined && filterValues.value !== '') {
            const suffix = {
              equals: '__eq',
              gt: '__gt',
              gte: '__gte',
              lt: '__lt',
              lte: '__lte',
            }[filterValues.operator];
            if (suffix) {
              params[`${apiKey}${suffix}`] = filterValues.value;
            }
          }
          break;
        default:
          if (filterValues.value !== undefined && filterValues.value !== '') {
            params[apiKey] = filterValues.value;
          }
          break;
      }
    });

    return params;
  }, [filters, config]);

  // Open filter popup
  const openFilterPopup = useCallback(
    (columnId, filterValues = {}) => {
      setOpenFilterColumn({ id: columnId });
      // Load existing filter values if any
      const existingFilter = filters[columnId];
      setTempFilters(existingFilter || filterValues);
    },
    [filters]
  );

  // Update temp filter values
  const updateTempFilter = useCallback((key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Remove filter by column
  const removeFilter = useCallback(columnId => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  }, []);

  // Clear current filter
  const clearCurrentFilter = useCallback(() => {
    if (openFilterColumn?.id) {
      removeFilter(openFilterColumn.id);
    }
    setOpenFilterColumn(null);
    setTempFilters({});
  }, [openFilterColumn, removeFilter]);

  // Apply filter
  const applyFilter = useCallback(
    (columnId, filterValues) => {
      // If called without parameters, use current openFilterColumn and tempFilters
      if (!columnId && openFilterColumn?.id) {
        const targetColumnId = openFilterColumn.id;
        const targetValues = tempFilters;

        // Check if there are valid values to apply the filter
        const hasValidValues = Object.keys(targetValues).some(key => {
          // For date filters, check if there's startDate or endDate
          if (key === 'startDate' || key === 'endDate') {
            return targetValues[key] && targetValues[key] !== '';
          }
          // For other filter types, check if value is not undefined or empty
          return targetValues[key] !== undefined && targetValues[key] !== '';
        });

        // Validate multiple values count for text filters
        if (hasValidValues && targetValues.value && typeof targetValues.value === 'string') {
          const hasMultipleValues =
            /[\s,\n\r]+/.test(targetValues.value) &&
            targetValues.value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

          if (hasMultipleValues) {
            const valueCount = targetValues.value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length;
            if (valueCount > 5000) {
              const errorMsg = `Cannot apply filter with ${valueCount} values. Maximum 5000 values allowed. Please split into multiple queries.`;
              if (onError) {
                onError(errorMsg, 'error');
              } else {
                alert(errorMsg);
              }
              return;
            }
          }
        }

        if (hasValidValues) {
          setFilters(prev => ({ ...prev, [targetColumnId]: targetValues }));
        }
        setOpenFilterColumn(null);
        setTempFilters({});
      } else if (columnId) {
        // Called with parameters - also validate here
        if (filterValues && filterValues.value && typeof filterValues.value === 'string') {
          const hasMultipleValues =
            /[\s,\n\r]+/.test(filterValues.value) &&
            filterValues.value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

          if (hasMultipleValues) {
            const valueCount = filterValues.value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length;
            if (valueCount > 5000) {
              const errorMsg = `Cannot apply filter with ${valueCount} values. Maximum 5000 values allowed. Please split into multiple queries.`;
              if (onError) {
                onError(errorMsg, 'error');
              } else {
                alert(errorMsg);
              }
              return;
            }
          }
        }

        setFilters(prev => ({ ...prev, [columnId]: filterValues }));
        setOpenFilterColumn(null);
        setTempFilters({});
      }
    },
    [openFilterColumn, tempFilters]
  );

  // Add filter (alias for applyFilter)
  const addFilter = useCallback(
    filter => {
      const { columnId, ...filterValues } = filter;
      applyFilter(columnId, filterValues);
    },
    [applyFilter]
  );

  // Clear filter by column (alias for removeFilter)
  const clearFilterByColumn = useCallback(
    columnId => {
      removeFilter(columnId);
    },
    [removeFilter]
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Clear persisted filters (for development/debugging)
  const clearPersistedFilters = useCallback(() => {
    if (!persistenceKey || typeof window === 'undefined') return;
    try {
      localStorage.removeItem(`filters_${persistenceKey}`);
      setFilters({});
    } catch (err) {
      // silent error
    }
  }, [persistenceKey]);

  // Check if column has filter
  const hasColumnFilter = useCallback(
    columnId => {
      return filters[columnId] && Object.keys(filters[columnId]).length > 0;
    },
    [filters]
  );

  // Get filter parameters for API calls
  const getFilterParams = useCallback(() => {
    return filterParams;
  }, [filterParams]);

  // Legacy support for getApiParams
  const getApiParams = useCallback(
    columnConfig => {
      if (columnConfig && columnConfig.length > 0) {
        // Use provided columnConfig if available
        const params = {};

        Object.entries(filters).forEach(([columnId, filterValues]) => {
          const column = columnConfig.find(col => col.id === columnId);
          if (!column) return;

          const { filterType, apiParam } = column;
          const apiKey = apiParam || columnId;

          switch (filterType) {
            case 'date':
              // For date filters, assume 'between' if startDate or endDate are present
              if (filterValues.startDate || filterValues.endDate || filterValues.operator === 'between') {
                if (filterValues.startDate) {
                  params[`${apiKey}__gte`] = filterValues.startDate;
                }
                if (filterValues.endDate) {
                  params[`${apiKey}__lte`] = filterValues.endDate;
                }
              } else if (filterValues.value) {
                const suffix = {
                  equals: '__eq',
                  after: '__gt',
                  before: '__lt',
                }[filterValues.operator];
                if (suffix) {
                  params[`${apiKey}${suffix}`] = filterValues.value;
                }
              }
              break;
            case 'text':
              if (filterValues.value) {
                const value = filterValues.value.trim();

                // Auto-detect if multiple values (contains comma, space, or line break separators)
                const hasMultipleValues =
                  /[\s,\n\r]+/.test(value) && value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

                if (hasMultipleValues) {
                  // Process multiple values separated by comma, space, or line break
                  const cleanedValues = value
                    .split(/[\s,\n\r]+/)
                    .map(v => v.trim())
                    .filter(v => v.length > 0);

                  if (cleanedValues.length > 0) {
                    params[`${apiKey}__in`] = cleanedValues.join(',');
                  }
                } else {
                  // Single value - use contains search
                  params[`${apiKey}__icontains`] = value;
                }
              }
              break;
            case 'select':
              if (filterValues.value !== undefined && filterValues.value !== '') {
                params[apiKey] = filterValues.value;
              }
              break;
            case 'number':
              if (filterValues.operator === 'between') {
                if (filterValues.min !== undefined && filterValues.min !== '') {
                  params[`${apiKey}__gte`] = filterValues.min;
                }
                if (filterValues.max !== undefined && filterValues.max !== '') {
                  params[`${apiKey}__lte`] = filterValues.max;
                }
              } else if (filterValues.value !== undefined && filterValues.value !== '') {
                const suffix = {
                  equals: '__eq',
                  greater_than: '__gt',
                  less_than: '__lt',
                  less_than_or_equal: '__lte',
                }[filterValues.operator];
                if (suffix) {
                  params[`${apiKey}${suffix}`] = filterValues.value;
                }
              }
              break;
            default:
              if (filterValues.value !== undefined && filterValues.value !== '') {
                params[apiKey] = filterValues.value;
              }
              break;
          }
        });

        return params;
      }
      return filterParams;
    },
    [filters, filterParams]
  );

  return {
    filters,
    activeFilters,
    filterParams,
    currentFilter,
    openFilterColumn,
    tempFilters,
    openFilterPopup,
    updateTempFilter,
    applyFilter,
    addFilter,
    removeFilter,
    clearCurrentFilter,
    clearAllFilters,
    clearFilterByColumn,
    clearPersistedFilters,
    hasColumnFilter,
    getFilterParams,
    getApiParams,
    setCurrentFilter,
  };
}

export default useColumnFilters;
